// Import necessary modules and components
import React, { useState, useEffect, useRef } from "react";
import styles from "./Chatbot.module.scss"; // Import CSS styles
import { useAppSelector } from "utils/hooks/storeHooks"; // Import a custom hook
import { useNavigate } from "react-router-dom"; // Import a routing hook
import { selectProviders } from "store/providers/selectors"; // Import a selector
import { useDispatch } from "react-redux"; // Import Redux's dispatch function
import { fetchAllProviders } from "store/providers/thunks"; // Import a Redux thunk
import { setEmbedToken } from "store/session"; // Import an action
import { ChatContext } from './Context';
import { handleRasaResponse, sendMessageToRasa, 
         removeDuplicateFilters, getProviderId } from '../../utils/chatbot/chatbotUtils';
import ChatHeader from "./ChatHeader";
import MessageList from "./MessageList";
import ChatInput from "./ChatToggleBox";
import ChatFooter from "./ChatFooter";

const ChatBot = () => {
  const endOfMessagesRef = useRef<HTMLDivElement | null>(null); 
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [messages, setMessages] = useState<any>([]);
  const [isChatboxActive, setIsChatboxActive] = useState<any>(false); 
  const [inputText, setInputText] = useState<any>(""); 
  const [currentReport, setCurrentReport] = useState<any>(); 
  const [prevMessages, setPrevMessages] = useState([]);

  const providers = useAppSelector(selectProviders);

  const scrollToBottom = () => {
    endOfMessagesRef.current?.scrollIntoView({ behavior: "smooth" });
    if (messagesContainerRef.current && endOfMessagesRef.current) {
      messagesContainerRef.current.scrollTop = endOfMessagesRef.current.offsetTop;
    }
  };

  const handleSendButtonClick = async () => {

    const addMessage = (message: any) => {
      setMessages((prevMessages: any) => [...prevMessages, message]);
    };

    if (inputText.trim() !== "") {
      const userMessage = { sender: "user", message: inputText };
      const messagetxt = inputText.trim();

      addMessage(userMessage);

      try {
        const response = await sendMessageToRasa("user", messagetxt);
        console.log("Rasa Response: ", response);
        handleRasaResponse(response, addMessage);
      } catch (error) {
          console.error("Error sending message to Rasa:", error);
      }
      setInputText("");
    }
  };

  const handleLoadReport = (values: any) => {

    console.log("Entro a handleLoadReport: ", values);

    const providerId = getProviderId(values?.data.provider);

    const filtersByGenie = values?.data.filters.map((filter: any) => {
      return {
        tableName: filter.table_name,
        columnName: filter.column_name,
        values: filter.values,
      };
    });

    const providerReportId = providers.find(
      (provider: any) => provider?.id === providerId
    )?.reportId;

    const parsedInfo = {
      providerId: providerId,
      genieFilters: filtersByGenie,
      reportId: providerReportId,
    };

    console.log("Parsed Info", parsedInfo);
    navigate("/Graphs", { state: parsedInfo });
  };

  const handleAddFilter = (values: any) => {
    const providerId = getProviderId(values?.data?.provider);

    const providerReportId = providers.find(
      (provider: any) => provider?.id === providerId
    )?.reportId;

    const filtersByGenie = values?.data?.filters.map((filter: any) => {
      return {
        tableName: filter.table_name,
        columnName: filter.column_name,
        values: filter.values,
      };
    });

    const parsedInfo = {
      addFilter: true,
      providerId: providerId,
      genieFilters: filtersByGenie,
      reportId: providerReportId,
    };

    navigate("/Graphs", { state: parsedInfo });
  };

  useEffect(() => {
    // Find all messages from the last render to now that may contain actionable data
    const newMessages = messages.slice(prevMessages.length);
  
    newMessages.forEach((message: any) => {
      if (message.data?.action === "report") {
        console.log("Entro a render de Reporte");
        setEmbedToken("");
        setCurrentReport(message);
        handleLoadReport(message);
      }
      if (
        message.data?.action === "addfilter" &&
        message.data?.provider === currentReport?.data?.provider
      ) {
        console.log("Entro a render de Reporte");
        message.data.filters = currentReport?.data.filters.concat(
          message.data.filters.filter((f: any) => f)
        );
        const filterParsed = removeDuplicateFilters(message);
        handleAddFilter(filterParsed);
      }
    });
  
    setPrevMessages(messages);
    dispatch(fetchAllProviders());
    scrollToBottom();
  }, [isChatboxActive, messages]);


  return (
    <ChatContext.Provider value={{ messages, setMessages, inputText, setInputText, isChatboxActive, 
                                   setIsChatboxActive, handleSendButtonClick, scrollToBottom }}>
      <div className={styles.container}>
        <div className={styles.chatbox}>
          {isChatboxActive && (
            <div className={styles.chatbox__support}>
              <ChatHeader />
              <MessageList
                messagesContainerRef={messagesContainerRef}
                endOfMessagesRef={endOfMessagesRef}
              />
              <ChatFooter />
            </div>
          )}
          <ChatInput />
        </div>
      </div>
    </ChatContext.Provider>
  );

};

export default ChatBot;
